import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MobileHeader.css';
import Logo from './Logo/Logo';
import { useDevNightMode } from '../hooks/useDevNightMode';
import { DayNightThemeToggle } from './DayNightThemeToggle/DayNightThemeToggle';
import ProPublicPageHeaderLink from './ProPublicPageHeaderLink';

import {
  Add,
  ArrowBack,
  FilterList,
  Menu,
  MoreVert,
  Search,
} from '@mui/icons-material';
interface MobileHeaderProps {
  title?: React.ReactNode;
  showBack?: boolean;
  showMenu?: boolean;
  showSearch?: boolean;
  showFilter?: boolean;
  showAdd?: boolean;
  showMore?: boolean;
  badgeCount?: number;
  onMenuClick?: () => void;
  onSearchClick?: () => void;
  onFilterClick?: () => void;
  onAddClick?: () => void;
  onMoreClick?: () => void;
  customActions?: React.ReactNode;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBack = false,
  showMenu = true,
  showSearch = false,
  showFilter = false,
  showAdd = false,
  showMore = false,
  badgeCount = 0,
  onMenuClick,
  onSearchClick,
  onFilterClick,
  onAddClick,
  onMoreClick,
  customActions
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['sidebar', 'profile', 'common', 'settings']);
  const {
    isDevNightModeAllowed,
    devNightModeEnabled,
    setDevNightModeEnabled,
  } = useDevNightMode();

  const getPageTitle = () => {
    if (title) return title;

    const path = location.pathname;
    if (path.includes('/planning')) return t('sidebar:menu.planning');
    if (path.includes('/reservations')) return t('sidebar:menu.reservations');
    if (path.includes('/clients')) return t('sidebar:menu.clients');
    if (path.includes('/chiens')) return t('common:mobileHeader.pageTitles.dogs');
    if (path.includes('/messages')) return t('sidebar:menu.messages');
    if (path.includes('/profil')) return t('profile:title');
    if (path.includes('/equipe')) return t('sidebar:menu.team');
    if (path.includes('/services')) return t('sidebar:menu.services');
    if (path.includes('/forfaits')) return t('sidebar:menu.packages');
    if (path.includes('/billing')) return t('sidebar:menu.billing');
    if (path.includes('/stats')) return t('sidebar:menu.statistics');
    if (path.includes('/settings')) return t('sidebar:menu.settings');
    if (path === '/app' || path === '/app/') return t('sidebar:menu.home');

    return 'RINTINTIN';
  };

  const handleBack = () => {
    navigate(-1);
  };

  const pageTitle = getPageTitle();

  return (
    <header className="mobile-header-advanced">
      <div className="mobile-header-left">
        {showBack ? (
          <button
            className="mobile-header-button"
            onClick={handleBack}
            aria-label={t('common:mobileHeader.back')}
          >
            <ArrowBack />
          </button>
        ) : showMenu ? (
          <button
            className="mobile-header-button"
            onClick={onMenuClick}
            aria-label={t('common:mobileHeader.menu')}
          >
            <Menu />
          </button>
        ) : (
          <div className="mobile-header-spacer" />
        )}
      </div>

      <h1 className="mobile-header-title">
        {pageTitle === 'RINTINTIN' ? (
          <Logo fontSize="1.3rem" showPro={true} />
        ) : (
          pageTitle
        )}
      </h1>

      <div className="mobile-header-right">
        <ProPublicPageHeaderLink variant="mobile" />
        {customActions || (
          <>
            {showSearch && (
              <button
                className="mobile-header-button"
                onClick={onSearchClick}
                aria-label={t('common:mobileHeader.search')}
              >
                <Search />
              </button>
            )}
            {showFilter && (
              <button
                className="mobile-header-button"
                onClick={onFilterClick}
                aria-label={t('common:mobileHeader.filter')}
                style={{ position: 'relative' }}
              >
                <FilterList />
                {badgeCount > 0 && (
                  <span className="mobile-header-badge">
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </button>
            )}
            {showAdd && (
              <button
                className="mobile-header-button"
                onClick={onAddClick}
                aria-label={t('common:mobileHeader.add')}
              >
                <Add />
              </button>
            )}
            {showMore && (
              <button
                className="mobile-header-button"
                onClick={onMoreClick}
                aria-label={t('common:mobileHeader.moreOptions')}
              >
                <MoreVert />
              </button>
            )}
            {!isDevNightModeAllowed &&
              !showSearch &&
              !showFilter &&
              !showAdd &&
              !showMore && <div className="mobile-header-spacer" />}
          </>
        )}
        {isDevNightModeAllowed && (
          <div
            className="mobile-header-theme-toggle-wrap"
            title={t('common:mobileHeader.nightMode')}
          >
            <DayNightThemeToggle
              checked={devNightModeEnabled}
              onChange={setDevNightModeEnabled}
              aria-label={t('settings:dayNightThemeToggle.toggleNightMode')}
            />
          </div>
        )}
      </div>
    </header>
  );
};

export default MobileHeader;
